import { useState, useEffect } from 'react';
import api from '../api';
import { useLang } from '../i18n/LanguageContext';

function fmt(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('uz-UZ', { day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit' });
}

function minutesUntil(iso) {
  if (!iso) return null;
  const diff = new Date(iso) - Date.now();
  return Math.floor(diff / 60000);
}

export default function BarPreOrders() {
  const { t } = useLang();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [labId, setLabId] = useState('');
  const [labs, setLabs] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [r, l] = await Promise.all([
        api.get(`/snack/pre-orders${labId ? `?lab_id=${labId}` : ''}`),
        api.get('/owner/labs'),
      ]);
      setOrders(r.data || []);
      setLabs(l.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [labId]);

  const ack = async (id, kind) => {
    try {
      await api.post(`/snack/pre-order/${id}/${kind}`);
      load();
    } catch (e) { alert(e.response?.data?.error || e.message); }
  };

  const markCash = async (id) => {
    if (!window.confirm(t('confirmMarkCash'))) return;
    try {
      await api.post(`/owner/snack-orders/${id}/mark-cash`);
      load();
    } catch (e) { alert(e.response?.data?.error || e.message); }
  };

  const cancelOrder = async (id) => {
    if (!window.confirm(t('confirmCancelOrder'))) return;
    try {
      await api.post(`/owner/snack-orders/${id}/cancel`);
      load();
    } catch (e) { alert(e.response?.data?.error || e.message); }
  };

  const active = orders.filter(o => !o.delivery_ack_at);
  const done = orders.filter(o => o.delivery_ack_at);

  return (
    <div style={{ padding: 20 }}>
      <h1 style={{ marginBottom: 16, fontSize: 22 }}>{t('barPreorders')}</h1>

      <div style={{ marginBottom: 16 }}>
        <select value={labId} onChange={e => setLabId(e.target.value)}
                style={{ padding: 8, background: 'var(--card)', border: '1px solid var(--border)', color: 'var(--text)' }}>
          <option value="">{t('allClubs')}</option>
          {labs.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
      </div>

      {loading ? <div>{t('loading')}</div>
       : active.length === 0 && done.length === 0
         ? <div style={{ padding: 40, textAlign: 'center', color: 'var(--text2)' }}>
             {t('noBarOrders')}
           </div>
         : (
         <>
           <h2 style={{ marginTop: 20, marginBottom: 12, color: 'var(--accent)' }}>
             {t('activeOrders')} ({active.length})
           </h2>
           {active.map(o => <OrderCard key={o.id} o={o} onAck={ack} onMarkCash={markCash} onCancel={cancelOrder} />)}

           {done.length > 0 && <>
             <h2 style={{ marginTop: 32, marginBottom: 12, color: 'var(--text2)' }}>
               {t('historyOrders')} ({done.length})
             </h2>
             {done.map(o => <OrderCard key={o.id} o={o} onAck={ack} archived />)}
           </>}
         </>
       )}
    </div>
  );
}

function OrderCard({ o, onAck, onMarkCash, onCancel, archived }) {
  const { t } = useLang();
  const mins = minutesUntil(o.booking_scheduled_at);
  const needsFirstAck = !o.admin_ack_at;
  const needsDeliveryAck = o.admin_ack_at && !o.delivery_ack_at;

  return (
    <div style={{
      background: 'var(--card)',
      border: '1px solid ' + (needsFirstAck ? '#FF1E6E' : needsDeliveryAck ? '#FFC53D' : 'var(--border)'),
      padding: 14,
      marginBottom: 10,
      opacity: archived ? 0.6 : 1,
    }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 8 }}>
        <div style={{ fontWeight: 700, fontSize: 18 }}>#{o.id}</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 600 }}>
            {o.user_name || 'Anonim'} · {t('booking')} #{o.booking_group_id}
            {o.computer_id && <span style={{ marginLeft: 8, color: '#27E0FF' }}>PC #{o.computer_id}</span>}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text2)' }}>
            {t('club')}: {o.lab_name} · {t('arrivalTime')}: {fmt(o.booking_scheduled_at)}
            {mins != null && !archived && <strong style={{ marginLeft: 8, color: mins < 30 ? '#FFC53D' : '#B6FF3C' }}>
              ({mins} {t('minsRemaining')})
            </strong>}
          </div>
        </div>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#B6FF3C' }}>
          {Number(o.total).toLocaleString()} sum
        </div>
      </div>

      {o.items && (
        <div style={{ padding: 10, background: 'var(--card2)', border: '1px dashed var(--border)', marginBottom: 8 }}>
          {o.items.map((it, i) => (
            <span key={i} style={{ marginRight: 12, fontSize: 13 }}>
              <strong>{it.qty}×</strong> {it.product_name}
            </span>
          ))}
        </div>
      )}

      {needsFirstAck && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={() => onAck(o.id, 'admin-ack')}
                  style={{ padding: '10px 20px', background: '#FF1E6E', color: '#fff',
                           border: 0, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
            ✅ {t('btnUnderstood')}
          </button>
          {onMarkCash && (
            <button onClick={() => onMarkCash(o.id)}
                    style={{ padding: '10px 16px', background: 'transparent', color: '#00A85A',
                             border: '1px solid #00A85A', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              💵 {t('btnMarkCash')}
            </button>
          )}
          {onCancel && (
            <button onClick={() => onCancel(o.id)}
                    style={{ padding: '10px 16px', background: 'transparent', color: '#FF3B5C',
                             border: '1px solid #FF3B5C', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              ✕ {t('btnCancelOrder')}
            </button>
          )}
        </div>
      )}
      {needsDeliveryAck && (
        <button onClick={() => onAck(o.id, 'delivery-ack')}
                style={{ padding: '10px 20px', background: '#FFC53D', color: '#000',
                         border: 0, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
          🎯 {t('btnDelivered')}
        </button>
      )}
      {o.delivery_ack_at && (
        <div style={{ color: '#B6FF3C', fontSize: 12 }}>
          ✅ {t('orderCompleted')}: {fmt(o.delivery_ack_at)}
        </div>
      )}
    </div>
  );
}
